import React, { useState, useEffect, useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { PlansTable } from './components/plans/PlansTable';
import { PlansDetails } from './components/plans/PlansDetails';
import { PlansModal } from './components/plans/PlansModal';
import { maintPlanService } from './services/maintPlanService';
import { Loader2, AlertCircle } from 'lucide-react';
import { useFactory } from '../../context/FactoryContext';


const DEFAULT_PAGE_SIZE = 5;
const PAGE_SIZE_OPTIONS = [5, 10];

const PlanesMantenimiento = () => {
  const queryClient = useQueryClient();
  const [searchQuery, setSearchQuery] = useState('');
  const [filterAsset, setFilterAsset] = useState('');
  const [filterPriority, setFilterPriority] = useState('');
  const [filterMaintenanceType, setFilterMaintenanceType] = useState('');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedProductId, setSelectedProductId] = useState(null);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const { selectedFactory } = useFactory();

  const [productToDelete, setProductToDelete] = useState(null);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);

  // Fetch maintenance plans
  const {
    data: plansResponse,
    isLoading: isLoadingPlans,
    isError: isErrorPlans,
    error: plansError
  } = useQuery({
    queryKey: ['plans', selectedFactory],
    queryFn: () => maintPlanService.getAll({ factory_code: selectedFactory }, 1, 500),
    retry: 1,
  });
  const plans2 = plansResponse?.data || [];

  //Everytime a new Plan is selected, we fetch it's details.
  useEffect(() => {
    const selectedPlan = plans2.find(p => p.id === selectedProductId) || null;
    setSelectedPlan(selectedPlan);
  }, [selectedProductId, plans2]);

  // Create mutation
  const createMutation = useMutation({
    mutationFn: (productData) => maintPlanService.create(productData),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['plans'] });
      setIsModalOpen(false);
      // Don't clear selectedProductId to keep the details panel open if a product was selected
    },
    onError: (error) => {
      console.error('Error creating plan:', error);
      throw error;
    },
  });

  // Update mutation
  const updateMutation = useMutation({
    mutationFn: ({ id, data }) => maintPlanService.update(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['plans'] });
      // Refresh the details panel
    },
    onError: (error) => {
      console.error('Error updating product:', error);
      throw error;
    },
  });

  // Delete mutation
  const deleteMutation = useMutation({
    mutationFn: (id) => maintPlanService.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['plans'] });
      setProductToDelete(null);
      // Clear selected product if it was deleted
      if (selectedProductId === productToDelete) {
        setSelectedProductId(null);
      }
    },
    onError: (error) => {
      console.error('Error deleting plan:', error);
      setProductToDelete(null);
      alert(error.message || 'Error deleting plan');
    },
  });

  //Handle to open creation Modal
  const handleCreate = () => {
    setSelectedProductId(null);
    setIsModalOpen(true);
  };

  //Handle to delete an existing plan
  const handleDelete = (productId) => {
    //Prevent missclicks
    if (window.confirm('Are you sure you want to delete this maintenance plan? This action cannot be undone.')) {
      setProductToDelete(productId);
      deleteMutation.mutate(productId);
    }
  };

  //Handle to create a new plan
  const handleSaveCreate = async (productData) => {
    await createMutation.mutateAsync(productData);
  };

  //Handle to update an existing plan
  const handleSaveUpdate = async (productData) => {
    if (selectedProductId) {
      await updateMutation.mutateAsync({
        id: selectedProductId,
        data: productData
      });
    }
  };

  //Handle to duplicate a plan
  const handleSaveDuplicate = async (productData) => {
    await createMutation.mutateAsync(productData);
  };

  //Handle to change selected plan upon click.
  const handleProductClick = (product) => {
    setSelectedProductId(product.id);
  };

  // Extract unique values for filters
  const uniqueAssets = React.useMemo(() => {
    const assets = [...new Set(plans2.map(p => p.asset_location).filter(Boolean))].sort();
    return assets;
  }, [plans2]);

  const uniquePriorities = React.useMemo(() => {
    const priorities = [...new Set(plans2.map(p => p.priority).filter(Boolean))].sort();
    return priorities;
  }, [plans2]);

  const uniqueMaintenanceTypes = React.useMemo(() => {
    const types = [...new Set(plans2.map(p => p.maintenance_type).filter(Boolean))].sort();
    return types;
  }, [plans2]);

  // Filter products based on search query and filters
  const filteredPlans = useMemo(() => {
    let filtered = plans2;

    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(plan =>
        plan.name?.toLowerCase().includes(query) ||
        //plan.product_type?.toLowerCase().includes(query) ||
        plan.maintenance_type?.toLowerCase().includes(query) ||
        plan.priority?.toLowerCase().includes(query) ||
        plan.description?.toLowerCase().includes(query)
      );
    }

    if (filterAsset) {
      filtered = filtered.filter(plan => plan.asset_location == filterAsset);
    }

    if (filterPriority) {
      filtered = filtered.filter(plan => plan.priority === filterPriority);
    }

    if (filterMaintenanceType) {
      filtered = filtered.filter(plan => plan.maintenance_type === filterMaintenanceType);
    }

    return filtered;
  }, [plans2, searchQuery, filterAsset, filterPriority, filterMaintenanceType]);

  // Pagination logic

  const totalItems = filteredPlans.length;
  const totalPages = Math.max(1, Math.ceil(totalItems / pageSize));
  const paginatedPlans = useMemo(
    () => filteredPlans.slice((page - 1) * pageSize, page * pageSize),
    [filteredPlans, page, pageSize]
  );
  const rangeStart = totalItems === 0 ? 0 : (page - 1) * pageSize + 1;
  const rangeEnd = Math.min(page * pageSize, totalItems);

  // Reset page to 1 when filters or page size change
  useEffect(() => {
    setPage(1);
  }, [searchQuery, filterAsset, filterPriority, filterMaintenanceType, pageSize]);

  const isLoading = isLoadingPlans
  const isError = isErrorPlans
  const error = plansError

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-center">
          <Loader2 className="w-12 h-12 text-blue-500 animate-spin mx-auto mb-4" />
          <p className="text-gray-400">Loading plans...</p>
        </div>
      </div>
    );
  }

  if (isError) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-center">
          <AlertCircle className="w-12 h-12 text-red-500 mx-auto mb-4" />
          <p className="text-red-400 mb-2">Error loading plans</p>
          <p className="text-gray-400 text-sm">{error?.message || 'Unknown error'}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-4 sm:p-6 w-full min-w-0">
      <div className="mb-6">
        <h1 className="text-3xl font-bold text-white mb-2">Plans Management</h1>
        <p className="text-gray-400">Manage maintenance plans and their specifications</p>
      </div>
      {/* Layout de dos columnas: Tabla izquierda y Detalles derecha */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6 items-stretch">
        {/* Columna izquierda: Tabla de planes */}
        <div className="min-w-0 flex flex-col min-h-0">
          <PlansTable
            //Plans
            products={paginatedPlans}
            //Pagination
            totalItems={totalItems}
            totalPages={totalPages}
            page={page}
            setPage={setPage}
            pageSize={pageSize}
            setPageSize={setPageSize}
            pageSizeOptions={PAGE_SIZE_OPTIONS}
            rangeStart={rangeStart}
            rangeEnd={rangeEnd}
            //Filters
            searchQuery={searchQuery}
            setSearchQuery={setSearchQuery}
            filterAsset={filterAsset}
            setFilterAsset={setFilterAsset}
            filterPriority={filterPriority}
            setFilterPriority={setFilterPriority}
            filterMaintenanceType={filterMaintenanceType}
            setFilterMaintenanceType={setFilterMaintenanceType}
            uniqueAssets={uniqueAssets}
            uniquePriorities={uniquePriorities}
            uniqueMaintenanceTypes={uniqueMaintenanceTypes}
            //Handler functions
            onProductClick={handleProductClick}
            onCreate={handleCreate}
            selectedProductId={selectedProductId}
          />
        </div>
        {/* Columna derecha: Detalles del producto */}
        <div className="min-w-0 flex flex-col items-stretch lg:sticky lg:top-6">
          <PlansDetails
            product={selectedPlan}
            loading={isLoadingPlans}
            onDelete={handleDelete}
            onSaveUpdate={handleSaveUpdate}
            onSaveDuplicate={handleSaveDuplicate}
          />
        </div>

      </div>
      {/* Modal para crear plan */}
      {isModalOpen && (
        <PlansModal
          isOpen={isModalOpen}
          onClose={() => {
            setIsModalOpen(false);
            // Solo limpiar selectedProductId si no hay producto seleccionado
            if (!selectedProductId) {
              setSelectedProductId(null);
            }
          }}
          onSave={handleSaveCreate}
        />
      )}

    </div>
  );
};

export default PlanesMantenimiento;
